import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';
import { scrollToAnchor } from '@theme/simucube-smooth-anchors';

class SimucubeAnchorNav extends HTMLElement {
  #observer = null;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    if (this.hasAttribute('data-scrollspy')) {
      this.#observe();
      scrollContainerMediaQuery.addEventListener('change', this.#reobserve);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    scrollContainerMediaQuery.removeEventListener('change', this.#reobserve);
    this.#observer?.disconnect();
  }

  get #links() {
    return [...this.querySelectorAll('a[href^="#"]')];
  }

  #targetFor(link) {
    const id = link.getAttribute('href').slice(1);
    return id ? document.getElementById(id) : null;
  }

  #handleClick = (event) => {
    const link = event.target.closest('a[href^="#"]');
    if (!link || !this.contains(link)) return;

    const target = this.#targetFor(link);
    if (!target) return;

    event.preventDefault();
    event.stopPropagation();
    scrollToAnchor(target, parseInt(this.dataset.scrollOffset, 10) || 0);
    history.replaceState(history.state, '', link.getAttribute('href'));
  };

  #observe() {
    this.#observer?.disconnect();

    const targets = this.#links.map((link) => this.#targetFor(link)).filter(Boolean);
    if (!targets.length) return;

    this.#observer = new IntersectionObserver(this.#handleIntersect, {
      root: getIntersectionRoot(),
      rootMargin: '-30% 0px -60% 0px',
    });
    for (const target of targets) this.#observer.observe(target);
  }

  #reobserve = () => {
    if (this.hasAttribute('data-scrollspy')) this.#observe();
  };

  #handleIntersect = (entries) => {
    const visible = entries.find((entry) => entry.isIntersecting);
    if (!visible) return;

    for (const link of this.#links) {
      const active = this.#targetFor(link) === visible.target;
      link.classList.toggle('is-active', active);
      if (active) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    }
  };
}

if (!customElements.get('simucube-anchor-nav')) {
  customElements.define('simucube-anchor-nav', SimucubeAnchorNav);
}
